import type {
	ContinuationFence,
	ContinuationFramePhase,
	ContinuationIdentity,
	ContinuationPhases,
	ContinuationReceiverPhase,
	ContinuationTarget
} from "~/helpers/pendingIntentLifecycle";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import transition, {
	exceedsChildEditorCap,
	isAllowlistedSourcePath,
	isStale,
	matchesIdentity,
	resolveReturnLandingIndex
} from "~/helpers/pendingIntentLifecycle";
import wsId from "~/helpers/wsId";
import tenantChanged from "~/store/tenantChanged";

export type ReturnRequestOrigin = "RETURN" | "BACK" | "SOURCE_DOCK";

export interface ContinuationReturnRequest {
	destinationPath: string;
	origin: ReturnRequestOrigin;
}

export interface ContinuationSavedResult {
	id: string;
	type: string;
}

export interface ContinuationCreatedRecord {
	id: string;
	linkedProductionItemNumbers?: string[];
	targetKind: string;
}

export interface ContinuationFrame {
	checkpoint?: unknown;
	countsTowardCap: boolean;
	createdRecord?: ContinuationCreatedRecord;
	editorKey: string;
	guardedPanelId?: string;
	kind: string;
	listContext?: unknown;
	listFilters?: Record<string, string>;
	navigationGeneration?: number;
	parentEditorKey?: string;
	phase: ContinuationFramePhase;
	receiverPhase: ContinuationReceiverPhase | null;
	requestGeneration?: number;
	returnFocusKey?: string;
	returnRequest?: ContinuationReturnRequest;
	sourceGeneration: number;
	sourceHistoryIndex?: number;
	sourcePath: string;
	targetId?: string;
	targetKind?: string;
}

export type ContinuationReceiverViewPhase = ContinuationReceiverPhase | "CHILD_EDITING";

export interface ContinuationReceiver {
	departed: boolean;
	editorKey: string;
	navigationGeneration: number;
	phase: ContinuationReceiverViewPhase;
	requestGeneration: number;
	targetId: string;
	targetKind: string;
}

export interface BrowsingLease {
	epoch: number;
	owner: string;
	priorBrowsingIds: string[];
	priorPath?: string;
}

export interface ResumeClaim {
	claimGeneration: number;
	editorKey: string;
	hostId: string;
}

export interface PendingIntentState {
	frames: ContinuationFrame[];
	generationCounter: number;
	identity?: ContinuationIdentity;
	lease?: BrowsingLease;
	resumeClaim?: ResumeClaim;
}

const initialState: PendingIntentState = {
	frames: [],
	generationCounter: 0
};

const nextGeneration = (state: PendingIntentState): number => {
	state.generationCounter += 1;
	return state.generationCounter;
};

const framePhases = (frame: ContinuationFrame): ContinuationPhases => ({
	receiverPhase: frame.receiverPhase,
	sourcePhase: frame.phase
});

const advance = (frame: ContinuationFrame, phases: ContinuationPhases): void => {
	frame.phase = phases.sourcePhase;
	frame.receiverPhase = phases.receiverPhase;
	if (phases.receiverPhase !== null) return;
	frame.createdRecord = undefined;
	frame.listFilters = undefined;
	frame.navigationGeneration = undefined;
	frame.requestGeneration = undefined;
	frame.returnRequest = undefined;
	frame.targetId = undefined;
	frame.targetKind = undefined;
};

const frameByKey = (frames: readonly ContinuationFrame[], editorKey: string): ContinuationFrame | undefined =>
	frames.find((frame) => frame.editorKey === editorKey);

const hopOwner = (frames: readonly ContinuationFrame[]): ContinuationFrame | undefined => {
	for (let index = frames.length - 1; index >= 0; index -= 1) {
		if (frames[index].receiverPhase !== null) return frames[index];
	}
	return undefined;
};

const topChildOf = (frames: readonly ContinuationFrame[], editorKey: string): ContinuationFrame | undefined => {
	for (let index = frames.length - 1; index >= 0; index -= 1) {
		if (frames[index].parentEditorKey === editorKey) return frames[index];
	}
	return undefined;
};

const landingFrame = (frames: readonly ContinuationFrame[]): ContinuationFrame | undefined => {
	const index = resolveReturnLandingIndex(frames);
	return index === -1 ? undefined : frames[index];
};

const removeFrameWithDescendants = (state: PendingIntentState, editorKey: string): void => {
	const removed = new Set([editorKey]);
	state.frames = state.frames.filter((frame) => {
		if (frame.parentEditorKey !== undefined && removed.has(frame.parentEditorKey)) removed.add(frame.editorKey);
		return !removed.has(frame.editorKey);
	});
};

const depart = (frame: ContinuationFrame): void => {
	const next = transition(framePhases(frame), "DEPART_TO_WORKSPACE");
	if (next) advance(frame, next);
};

const closeFrame = (state: PendingIntentState, frame: ContinuationFrame): void => {
	const next = transition(framePhases(frame), "CLOSE_SOURCE");
	if (!next) return;
	if (frame.parentEditorKey !== undefined) {
		removeFrameWithDescendants(state, frame.editorKey);
		return;
	}
	frame.checkpoint = undefined;
	frame.listContext = undefined;
	advance(frame, next);
	state.frames.splice(1);
};

const settleClaimedReturn = (state: PendingIntentState): void => {
	const claimed = state.resumeClaim && frameByKey(state.frames, state.resumeClaim.editorKey);
	const acknowledged = claimed && transition(framePhases(claimed), "ACKNOWLEDGE_RESTORED_HOST");
	if (!claimed || !acknowledged) return;
	state.resumeClaim = undefined;
	advance(claimed, acknowledged);
	const settled = claimed.parentEditorKey === undefined ? claimed : topChildOf(state.frames, claimed.editorKey);
	if (settled) closeFrame(state, settled);
};

export const pendingIntentSlice = createSlice({
	extraReducers: (builder) => {
		builder.addCase(tenantChanged, (state) => ({ ...initialState, generationCounter: state.generationCounter }));
	},
	initialState,
	name: "pendingIntent",
	reducers: {
		abandonPreparation: (state, action: PayloadAction<{ editorKey: string; identity: ContinuationIdentity }>) => {
			if (!matchesIdentity(action.payload.identity, state.identity)) return;
			const frame = frameByKey(state.frames, action.payload.editorKey);
			const next = frame && transition(framePhases(frame), "ABANDON_PREPARATION");
			if (!next || !frame) return;
			frame.checkpoint = undefined;
			frame.listContext = undefined;
			advance(frame, next);
		},

		acknowledgeRestoredHost: (
			state,
			action: PayloadAction<{ claimGeneration: number; hostId: string; identity: ContinuationIdentity }>
		) => {
			if (!matchesIdentity(action.payload.identity, state.identity)) return;
			if (state.resumeClaim?.hostId !== action.payload.hostId) return;
			if (state.resumeClaim.claimGeneration !== action.payload.claimGeneration) return;
			const frame = frameByKey(state.frames, state.resumeClaim.editorKey);
			const next = frame && transition(framePhases(frame), "ACKNOWLEDGE_RESTORED_HOST");
			if (!next || !frame) return;
			state.resumeClaim = undefined;
			advance(frame, next);
		},

		acquireBrowsingLease: (
			state,
			action: PayloadAction<{
				identity: ContinuationIdentity;
				owner: string;
				priorBrowsingIds: string[];
				priorPath?: string;
			}>
		) => {
			if (!matchesIdentity(action.payload.identity, state.identity)) return;
			state.lease = {
				epoch: nextGeneration(state),
				owner: action.payload.owner,
				priorBrowsingIds: action.payload.priorBrowsingIds,
				priorPath: action.payload.priorPath
			};
		},

		bumpRequestGeneration: (state, action: PayloadAction<{ identity: ContinuationIdentity }>) => {
			if (!matchesIdentity(action.payload.identity, state.identity)) return;
			const owner = hopOwner(state.frames);
			if (!owner) return;
			owner.requestGeneration = nextGeneration(state);
		},

		claimResume: (state, action: PayloadAction<{ hostId: string; identity: ContinuationIdentity }>) => {
			if (!matchesIdentity(action.payload.identity, state.identity)) return;
			const frame = landingFrame(state.frames);
			if (!frame || frame.checkpoint === undefined) return;
			const next = transition(framePhases(frame), "CLAIM_RESUME");
			if (!next) return;
			state.resumeClaim = {
				claimGeneration: nextGeneration(state),
				editorKey: frame.editorKey,
				hostId: action.payload.hostId
			};
			frame.returnRequest = undefined;
			advance(frame, next);
		},

		closeChildEditing: (state, action: PayloadAction<{ editorKey: string; identity: ContinuationIdentity }>) => {
			if (!matchesIdentity(action.payload.identity, state.identity)) return;
			const frame = frameByKey(state.frames, action.payload.editorKey);
			if (!frame || frame.parentEditorKey === undefined || frame.phase !== "ACTIVE") return;
			removeFrameWithDescendants(state, frame.editorKey);
		},

		closeSource: (state, action: PayloadAction<{ editorKey: string; identity: ContinuationIdentity }>) => {
			if (!matchesIdentity(action.payload.identity, state.identity)) return;
			const frame = frameByKey(state.frames, action.payload.editorKey);
			if (frame) closeFrame(state, frame);
		},

		consumePendingNavigation: (
			state,
			action: PayloadAction<{ identity: ContinuationIdentity; navigationGeneration: number } & ContinuationTarget>
		) => {
			if (!matchesIdentity(action.payload.identity, state.identity)) return;
			const owner = hopOwner(state.frames);
			if (!owner) return;
			if (owner.navigationGeneration !== action.payload.navigationGeneration) return;
			if (owner.targetId !== action.payload.targetId) return;
			if (owner.targetKind !== action.payload.targetKind) return;
			const next = transition(framePhases(owner), "CONSUME_PENDING_NAVIGATION");
			if (next) advance(owner, next);
		},

		establishSessionIdentity: {
			prepare: (payload: { accountEmail: string; tenantId: string }) => ({
				payload: { ...payload, sessionNonce: wsId() }
			}),
			reducer: (state, action: PayloadAction<ContinuationIdentity>) => ({
				...initialState,
				generationCounter: state.generationCounter,
				identity: action.payload
			})
		},

		invalidateIdentity: (state) => ({ ...initialState, generationCounter: state.generationCounter }),

		openChildEditing: (
			state,
			action: PayloadAction<{
				countsTowardCap: boolean;
				editorKey: string;
				identity: ContinuationIdentity;
				kind: string;
				panelId: string;
				returnFocusKey?: string;
				sourcePath: string;
			}>
		) => {
			if (!matchesIdentity(action.payload.identity, state.identity)) return;
			if (frameByKey(state.frames, action.payload.editorKey)) return;
			const owner = hopOwner(state.frames);
			if (!owner || owner.phase !== "SUSPENDED" || owner.receiverPhase !== "INSPECTING") return;
			if (action.payload.countsTowardCap && exceedsChildEditorCap(state.frames)) {
				depart(owner);
				return;
			}
			state.frames.push({
				countsTowardCap: action.payload.countsTowardCap,
				editorKey: action.payload.editorKey,
				guardedPanelId: action.payload.panelId,
				kind: action.payload.kind,
				parentEditorKey: owner.editorKey,
				phase: "ACTIVE",
				receiverPhase: null,
				returnFocusKey: action.payload.returnFocusKey,
				sourceGeneration: nextGeneration(state),
				sourcePath: action.payload.sourcePath
			});
		},

		prepareSource: (
			state,
			action: PayloadAction<{
				allowedSourcePathPrefixes: readonly string[];
				checkpoint: unknown;
				editorKey: string;
				identity: ContinuationIdentity;
				kind: string;
				listContext: unknown;
				returnFocusKey?: string;
				sourceHistoryIndex?: number;
				sourcePath: string;
			}>
		) => {
			if (!matchesIdentity(action.payload.identity, state.identity)) return;
			if (!isAllowlistedSourcePath(action.payload.sourcePath, action.payload.allowedSourcePathPrefixes)) return;
			settleClaimedReturn(state);
			const existing = frameByKey(state.frames, action.payload.editorKey);
			if (existing && transition(framePhases(existing), "PREPARE_SOURCE")) {
				existing.checkpoint = action.payload.checkpoint;
				existing.kind = action.payload.kind;
				existing.listContext = action.payload.listContext;
				existing.returnFocusKey = action.payload.returnFocusKey;
				existing.sourceGeneration = nextGeneration(state);
				existing.sourceHistoryIndex = action.payload.sourceHistoryIndex;
				existing.sourcePath = action.payload.sourcePath;
				state.resumeClaim = undefined;
				return;
			}
			state.frames = [
				{
					checkpoint: action.payload.checkpoint,
					countsTowardCap: false,
					editorKey: action.payload.editorKey,
					kind: action.payload.kind,
					listContext: action.payload.listContext,
					phase: "ACTIVE",
					receiverPhase: null,
					returnFocusKey: action.payload.returnFocusKey,
					sourceGeneration: nextGeneration(state),
					sourceHistoryIndex: action.payload.sourceHistoryIndex,
					sourcePath: action.payload.sourcePath
				}
			];
			state.resumeClaim = undefined;
		},

		recordCreatedRecord: (
			state,
			action: PayloadAction<{
				identity: ContinuationIdentity;
				record: ContinuationCreatedRecord;
				requestGeneration: number;
			}>
		) => {
			if (!matchesIdentity(action.payload.identity, state.identity)) return;
			const owner = hopOwner(state.frames);
			if (!owner || owner.targetKind !== action.payload.record.targetKind) return;
			if (owner.requestGeneration !== action.payload.requestGeneration) return;
			owner.createdRecord = action.payload.record;
		},

		releaseBrowsingLease: (state, action: PayloadAction<{ epoch: number; owner: string }>) => {
			if (state.lease?.owner !== action.payload.owner) return;
			if (state.lease.epoch !== action.payload.epoch) return;
			state.lease = undefined;
		},

		requestReturn: (
			state,
			action: PayloadAction<{
				identity: ContinuationIdentity;
				request: ContinuationReturnRequest;
				requestGeneration: number | undefined;
			}>
		) => {
			if (!matchesIdentity(action.payload.identity, state.identity)) return;
			if (hopOwner(state.frames)?.requestGeneration !== action.payload.requestGeneration) return;
			const frame = landingFrame(state.frames);
			if (!frame) return;
			frame.returnRequest = action.payload.request;
		},

		settleUnfinishedReturn: (state, action: PayloadAction<{ identity: ContinuationIdentity }>) => {
			if (matchesIdentity(action.payload.identity, state.identity)) settleClaimedReturn(state);
		},

		suspendSource: (
			state,
			action: PayloadAction<{ editorKey: string; identity: ContinuationIdentity } & ContinuationTarget>
		) => {
			if (!matchesIdentity(action.payload.identity, state.identity)) return;
			if (!state.lease) return;
			const frame = frameByKey(state.frames, action.payload.editorKey);
			if (!frame || frame.checkpoint === undefined) return;
			const next = transition(framePhases(frame), "SUSPEND_SOURCE");
			if (!next) return;
			advance(frame, next);
			frame.listFilters = action.payload.listFilters;
			frame.navigationGeneration = nextGeneration(state);
			frame.requestGeneration = nextGeneration(state);
			frame.targetId = action.payload.targetId;
			frame.targetKind = action.payload.targetKind;
		}
	}
});

export const {
	abandonPreparation,
	acknowledgeRestoredHost,
	acquireBrowsingLease,
	bumpRequestGeneration,
	claimResume,
	closeChildEditing,
	closeSource,
	consumePendingNavigation,
	establishSessionIdentity,
	invalidateIdentity,
	openChildEditing,
	prepareSource,
	recordCreatedRecord,
	releaseBrowsingLease,
	requestReturn,
	settleUnfinishedReturn,
	suspendSource
} = pendingIntentSlice.actions;

export interface PendingIntentRootState {
	pendingIntent: PendingIntentState;
}

export interface ClaimableSource<TDraft, TListContext> {
	checkpoint: TDraft;
	createdRecord?: ContinuationCreatedRecord;
	editorKey: string;
	kind: string;
	listContext: TListContext;
	returnFocusKey?: string;
	sourceGeneration: number;
	sourcePath: string;
	target?: ContinuationTarget;
}

export const selectContinuationIdentity = (state: PendingIntentRootState): ContinuationIdentity | undefined =>
	state.pendingIntent.identity;

export const selectBrowsingLease = (state: PendingIntentRootState): BrowsingLease | undefined =>
	state.pendingIntent.lease;

export const selectResumeClaim = (state: PendingIntentRootState): ResumeClaim | undefined =>
	state.pendingIntent.resumeClaim;

export const selectFrame = (state: PendingIntentRootState, editorKey: string): ContinuationFrame | undefined =>
	frameByKey(state.pendingIntent.frames, editorKey);

export const selectOpenChildFrame = (
	state: PendingIntentRootState,
	parentEditorKey: string
): ContinuationFrame | undefined => topChildOf(state.pendingIntent.frames, parentEditorKey);

export const selectOpenChildEditorKey = (
	state: PendingIntentRootState,
	scope: { kind: string; panelId: string }
): string | undefined => {
	const { frames } = state.pendingIntent;
	for (let index = frames.length - 1; index >= 0; index -= 1) {
		const frame = frames[index];
		if (frame.parentEditorKey === undefined || frame.phase !== "ACTIVE") continue;
		if (frame.guardedPanelId !== scope.panelId || frame.kind !== scope.kind) continue;
		return frame.editorKey;
	}
	return undefined;
};

export const selectChildFrameIsOpen = (state: PendingIntentRootState, editorKey: string): boolean => {
	const frame = frameByKey(state.pendingIntent.frames, editorKey);
	return frame?.parentEditorKey !== undefined && frame.phase === "ACTIVE";
};

export const selectEvictionGuardedPanelId = (state: PendingIntentRootState): string | undefined => {
	const { frames } = state.pendingIntent;
	for (let index = frames.length - 1; index >= 0; index -= 1) {
		if (frames[index].parentEditorKey !== undefined) return frames[index].guardedPanelId;
	}
	return undefined;
};

export const selectOwnsBrowsingLease = (
	state: PendingIntentRootState,
	claim: { epoch: number; owner: string }
): boolean => state.pendingIntent.lease?.owner === claim.owner && state.pendingIntent.lease.epoch === claim.epoch;

export const selectRestorableBrowsingState = (
	state: PendingIntentRootState,
	claim: { epoch: number; owner: string }
): { priorBrowsingIds: string[]; priorPath?: string } | undefined => {
	const { lease } = state.pendingIntent;
	if (!lease || lease.owner !== claim.owner || lease.epoch !== claim.epoch) return undefined;
	return { priorBrowsingIds: lease.priorBrowsingIds, priorPath: lease.priorPath };
};

export const selectReturnLandingFrame = (
	state: PendingIntentRootState,
	identity: ContinuationIdentity | undefined
): ContinuationFrame | undefined => {
	if (!matchesIdentity(identity, state.pendingIntent.identity)) return undefined;
	return landingFrame(state.pendingIntent.frames);
};

export const selectHonoredReturnRequest = (
	state: PendingIntentRootState,
	identity: ContinuationIdentity | undefined
): ContinuationReturnRequest | undefined => {
	const frame = selectReturnLandingFrame(state, identity);
	if (!frame?.returnRequest) return undefined;
	return frame.returnRequest;
};

export const liveReceiverTarget = (pendingIntent: PendingIntentState): ContinuationTarget | undefined => {
	const owner = hopOwner(pendingIntent.frames);
	return owner?.targetId !== undefined && owner.targetKind !== undefined
		? { listFilters: owner.listFilters, targetId: owner.targetId, targetKind: owner.targetKind }
		: undefined;
};

export const selectLiveRequestGeneration = (state: PendingIntentRootState): number | undefined =>
	hopOwner(state.pendingIntent.frames)?.requestGeneration;

export const selectLiveReceiverContinuation = (
	state: PendingIntentRootState,
	query: { identity: ContinuationIdentity | undefined } & ContinuationTarget
): ContinuationReceiver | undefined => {
	const { frames, identity } = state.pendingIntent;
	if (!matchesIdentity(query.identity, identity)) return undefined;
	for (let index = frames.length - 1; index >= 0; index -= 1) {
		const frame = frames[index];
		if (frame.receiverPhase === null || frame.navigationGeneration === undefined) continue;
		if (frame.requestGeneration === undefined) continue;
		if (frame.targetId !== query.targetId || frame.targetKind !== query.targetKind) continue;
		return {
			departed: frame.phase === "DEPARTED_TO_WORKSPACE",
			editorKey: frame.editorKey,
			navigationGeneration: frame.navigationGeneration,
			phase: topChildOf(frames, frame.editorKey) ? "CHILD_EDITING" : frame.receiverPhase,
			requestGeneration: frame.requestGeneration,
			targetId: frame.targetId,
			targetKind: frame.targetKind
		};
	}
	return undefined;
};

const CLAIMABLE_PHASES: ContinuationFramePhase[] = ["SUSPENDED", "RESUMING", "DEPARTED_TO_WORKSPACE"];

export const selectClaimableSource = (
	state: PendingIntentRootState,
	query: { editorKey?: string; identity: ContinuationIdentity | undefined }
): ClaimableSource<unknown, unknown> | undefined => {
	const { frames, identity } = state.pendingIntent;
	if (!matchesIdentity(query.identity, identity)) return undefined;
	const frame = landingFrame(frames);
	if (!frame || frame.checkpoint === undefined) return undefined;
	if (query.editorKey !== undefined && query.editorKey !== frame.editorKey) return undefined;
	if (!CLAIMABLE_PHASES.includes(frame.phase)) return undefined;
	return {
		checkpoint: frame.checkpoint,
		createdRecord: frame.createdRecord,
		editorKey: frame.editorKey,
		kind: frame.kind,
		listContext: frame.listContext,
		returnFocusKey: frame.returnFocusKey,
		sourceGeneration: frame.sourceGeneration,
		sourcePath: frame.sourcePath,
		target:
			frame.targetId !== undefined && frame.targetKind !== undefined
				? { targetId: frame.targetId, targetKind: frame.targetKind }
				: undefined
	};
};

export const narrowClaimableSource = <TDraft, TListContext>(
	claimable: ClaimableSource<unknown, unknown> | undefined,
	isCheckpoint: (checkpoint: unknown) => checkpoint is TDraft,
	isListContext: (listContext: unknown) => listContext is TListContext
): ClaimableSource<TDraft, TListContext> | undefined => {
	if (!claimable) return undefined;
	if (!isCheckpoint(claimable.checkpoint) || !isListContext(claimable.listContext)) return undefined;
	return { ...claimable, checkpoint: claimable.checkpoint, listContext: claimable.listContext };
};

export const selectContinuationResultIsStale = (state: PendingIntentRootState, captured: ContinuationFence): boolean =>
	isStale(captured, {
		epoch: state.pendingIntent.lease?.epoch,
		generation: selectLiveRequestGeneration(state),
		identity: state.pendingIntent.identity,
		owner: state.pendingIntent.lease?.owner
	});

export default pendingIntentSlice.reducer;
